import { useState, useEffect, createContext, useContext } from 'react';
import type { ReactNode } from 'react';

interface LoginRequest {
  email: string;
  password: string;
  tenantId?: string;
}

interface LoginResponse {
  token: string;
  email: string;
  tenantId: string;
  roleId: number;
  superAdmin: boolean;
}

interface User {
  email: string;
  tenantId: string;
  roleId: number;
  superAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token management utilities
const TOKEN_KEY = 'poolchem_token';
const USER_KEY = 'poolchem_user';

const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const getStoredUser = (): User | null => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

const setStoredToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Handle storage errors silently
  }
};

const setStoredUser = (user: User): void => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    // Handle storage errors silently
  }
};

const removeStoredAuth = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // Handle storage errors silently
  }
};

// JWT token validation
const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize auth state from localStorage
    const initializeAuth = () => {
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();

      if (storedToken && storedUser && isTokenValid(storedToken)) {
        setToken(storedToken);
        setUser(storedUser);
      } else {
        // Clear invalid/expired data
        removeStoredAuth();
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      // Import authApi dynamically to avoid circular dependency
      const { authApi } = await import('../services/authService');
      const data = await authApi.login(credentials);

      const userData: User = {
        email: data.email,
        tenantId: data.tenantId,
        roleId: data.roleId,
        superAdmin: data.superAdmin,
      };

      setToken(data.token);
      setUser(userData);
      setStoredToken(data.token);
      setStoredUser(userData);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = (): void => {
    setToken(null);
    setUser(null);
    removeStoredAuth();
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;